import { Flex, Skeleton } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router';
import { z } from 'zod';

import { utils as commonUtils } from '@finley-technologies/common';

import { CreditFacilitiesContextValue } from './types';
import { useCreditFacilities } from './useCreditFacilities';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { DefaultErrorRoute } from '~/core/DefaultErrorRoute';

// Constants
const defaultContextValue: CreditFacilitiesContextValue = {
  status: 'loading',
  facilities: null,
  error: null,
};

// Contexts
export const CreditFacilitiesContext = createContext<CreditFacilitiesContextValue>(
  defaultContextValue,
);

// Fragments
const LoadingState = () => {
  const { user } = useUserAuthOrThrow();
  const location = useLocation();

  // Borrower routes that add non-standard padding
  const borrowerNoPaddingRoutes = [
    /^\/$/,
    /^\/credit-agreements$/,
    /^\/credit-facility-receivables$/,
    /^\/strats$/,
    /^\/insights$/,
    /^\/statements$/,
  ];

  const doNotUsePaddingForRoute =
    user.userType === 'BORROWER'
      ? borrowerNoPaddingRoutes.some((noPaddingRoutePrefix) =>
          noPaddingRoutePrefix.test(location.pathname),
        )
      : false;

  return (
    <Flex
      direction="column"
      padding={
        doNotUsePaddingForRoute ? '0' : ['1.5rem', '1.5rem', '1.5rem', '2.5rem']
      }
    >
      <Skeleton height="20rem" />
    </Flex>
  );
};

// Exports
type Props = { children: React.ReactNode };
export const CreditFacilitiesProvider = ({ children }: Props) => {
  const facilitiesQuery = useCreditFacilities();
  const facilities = facilitiesQuery.data?.organization.creditFacilities;

  // Assemble the context value
  const contextValue: CreditFacilitiesContextValue = useMemo(() => {
    if (facilitiesQuery.error) {
      // Handle errors
      Sentry.captureException(facilitiesQuery.error);
      return {
        status: 'error',
        facilities: null,
        error: facilitiesQuery.error,
      };
    }
    if (facilities) {
      return {
        status: 'ready',
        facilities,
        error: null,
      };
    }
    return {
      status: 'loading',
      facilities: null,
      error: null,
    };
  }, [facilitiesQuery.error, facilities]);

  // Logging state for debugging purposes
  useEffect(() => {
    if (!commonUtils.EnvUtils.isCustomerFacing()) {
      // eslint-disable-next-line no-console
      console.log('[CreditFacilitiesProvider] Context change', {
        status: contextValue.status,
        facilities: contextValue.facilities,
      });
    }
  }, [contextValue]);

  return (
    <CreditFacilitiesContext.Provider value={contextValue}>
      {(() => {
        if (contextValue.status === 'ready') return <>{children}</>;
        if (contextValue.status === 'error') return <DefaultErrorRoute />;
        return <LoadingState />;
      })()}
    </CreditFacilitiesContext.Provider>
  );
};

/**
 * Returns credit facilities. Throws if on a route not wrapped in `<CreditFacilitiesProvider />`.
 */
export const useCreditFacilitiesOrThrow = () => {
  const contextValue = useContext(CreditFacilitiesContext);
  if (contextValue.status !== 'ready') {
    throw Error('Expected credit facilities to be available');
  }
  return contextValue;
};

const CreditFacilityParamsStruct = z.object({
  creditFacilityId: z.string({ message: 'No facility selected' }),
});
export const useCreditFacilityOrThrow = (facilityId: string) => {
  const { facilities } = useCreditFacilitiesOrThrow();
  const selectedFacility = facilities.find(
    (facility) => facility.id === facilityId,
  );
  if (!selectedFacility) {
    throw Error(`Expected selected [facility:${facilityId}] to be available`);
  }
  return selectedFacility;
};
export const useRouteCreditFacilityOrThrow = () => {
  const params = useParams();
  const { creditFacilityId } = CreditFacilityParamsStruct.parse(params);
  return useCreditFacilityOrThrow(creditFacilityId);
};
